/**
 * Bulk Pricing Discount Manager
 * Applies Shopify discount codes when every eligible line shares one tier,
 * and renders progress without simulating prices in the browser.
 */

class BulkPricingDiscountManager {
  constructor() {
    this.isProcessing = false;
    this.isApplyingDiscount = false;
    this.productEligibilityCache = new Map();
    this.lastProcessTime = 0;
    this.processCooldown = 2000; // 2 seconds cooldown between processes
    this.pendingCartUpdateTimeoutId = null;
    this.tierRenderTimeoutId = null;
    this.init();
  }

  init() {
    // Listen for cart updates to show tier progress
    document.addEventListener('cart:updated', this.handleCartUpdate.bind(this));
    document.addEventListener('cart:refresh', this.handleCartUpdate.bind(this));
    document.addEventListener('cart:update-display', this.handleCartUpdate.bind(this));
    
    // Listen for quantity changes
    this.initQuantityChangeListeners();
    
    // Apply on page load
    setTimeout(() => this.handleCartUpdate(), 1000);
    
    // Show tier progress in mini cart
    this.initMiniCartTierProgress();
    
    // Apply proper bulk pricing discounts
    this.applyBulkPricingDiscounts();

    // Re-apply tier progress when the cart DOM changes
    this.observeCartDomChanges();
    
    // Intercept cart update errors and show them near specific products
    this.interceptCartErrors();
  }

  isCartPage() {
    const path = window.location.pathname || '';
    return /(^|\/)cart(\/|$)/.test(path);
  }

  interceptCartErrors() {
    // 2026-07 (P0 root-cause fix): removed the window.fetch monkey-patch.
    // It wrapped EVERY cart mutation — including the theme's own /cart/change.js
    // quantity updates — and threw on non-ok responses, which blocked quantity
    // edits. It also fired the now-retired discount cascade on every mutation.
    // Patching window.fetch is inherently fragile (kt-cart-page.js deliberately
    // captures the native fetch to avoid such wrappers). Cart errors surface
    // through the theme's own handling; we keep only the passive DOM watcher.
    //
    // Tier-progress display refreshes via the cart:updated / cart:refresh /
    // cart:update-display listeners and on page load — no fetch hook needed now
    // that this manager performs no cart writes.
    this.monitorForErrorMessages();
  }

  monitorForErrorMessages() {
    // Use MutationObserver to detect when error messages appear
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            // Check if this is an error message
            if (node.classList && (
              node.classList.contains('t4s-notices__mess') ||
              node.classList.contains('t4s-cart__errors') ||
              node.querySelector('.t4s-notices__mess')
            )) {
              // Find the error message
              const errorMessage = node.classList.contains('t4s-notices__mess') 
                ? node 
                : node.querySelector('.t4s-notices__mess');
              
              if (errorMessage && errorMessage.textContent.includes('Not enough items available')) {
                // Move this error message near the last updated product
                this.moveErrorToProduct(errorMessage);
              }
            }
          }
        });
      });
    });
    
    // Start observing
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  moveErrorToProduct(errorMessage) {
    // Find the last updated cart item (most recently interacted with)
    const cartItems = document.querySelectorAll('[data-cart-item]');
    let targetItem = null;
    let targetQuantityInput = null;
    
    // Try to find the item that was most recently updated
    for (let item of cartItems) {
      const quantityInput = item.querySelector('input[type="number"]');
      if (quantityInput && quantityInput.dataset.lastUpdated) {
        targetItem = item;
        targetQuantityInput = quantityInput;
        break;
      }
    }
    
    // If no specific item found, use the first cart item
    if (!targetItem && cartItems.length > 0) {
      targetItem = cartItems[0];
      targetQuantityInput = targetItem.querySelector('input[type="number"]');
    }
    
    if (targetItem) {
      // Remove any existing error messages for this item
      const existingError = targetItem.querySelector('.product-error-message');
      if (existingError) {
        existingError.remove();
      }
      
      // Create a new error message near the product
      const newErrorDiv = document.createElement('div');
      newErrorDiv.className = 'product-error-message persistent-error';
      newErrorDiv.textContent = errorMessage.textContent;
      newErrorDiv.dataset.itemKey = targetItem.dataset.cartItem || 'unknown';
      
      // Insert after the cart item
      targetItem.insertAdjacentElement('afterend', newErrorDiv);
      
      // Set up quantity change listener to remove error when quantity is fixed
      if (targetQuantityInput) {
        const removeErrorOnValidQuantity = () => {
          const currentQuantity = parseInt(targetQuantityInput.value) || 0;
          // Extract available quantity from error message (e.g., "Only 17 left")
          const availableMatch = errorMessage.textContent.match(/(\d+)\s+left/);
          const availableQuantity = availableMatch ? parseInt(availableMatch[1]) : 0;
          
          if (currentQuantity <= availableQuantity && currentQuantity > 0) {
            // Quantity is now valid, remove the error
            if (newErrorDiv.parentNode) {
              newErrorDiv.remove();
            }
            // Remove the event listener
            targetQuantityInput.removeEventListener('change', removeErrorOnValidQuantity);
            targetQuantityInput.removeEventListener('input', removeErrorOnValidQuantity);
          }
        };
        
        // Listen for quantity changes
        targetQuantityInput.addEventListener('change', removeErrorOnValidQuantity);
        targetQuantityInput.addEventListener('input', removeErrorOnValidQuantity);
      }
    }
    
    // Hide the original error message
    errorMessage.style.display = 'none';
    if (errorMessage.parentNode) {
      errorMessage.parentNode.style.display = 'none';
    }
  }

  showProductSpecificError(errorData, options) {
    // Remove any existing product-specific error messages
    document.querySelectorAll('.product-error-message').forEach(msg => msg.remove());
    
    // Find the cart item being updated
    let targetItem = null;
    
    if (options && options.body) {
      try {
        const bodyData = JSON.parse(options.body);
        if (bodyData.id) {
          // Find cart item by variant ID
          targetItem = document.querySelector(`[data-variant-id="${bodyData.id}"]`);
        } else if (bodyData.updates) {
          // Find cart item by key
          const itemKey = Object.keys(bodyData.updates)[0];
          targetItem = document.querySelector(`[data-cart-item-key="${itemKey}"]`);
        }
      } catch (e) {
        // Handle form data
        if (options.body instanceof FormData) {
          const itemKey = options.body.get('id') || options.body.get('updates[]');
          if (itemKey) {
            targetItem = document.querySelector(`[data-variant-id="${itemKey}"]`) || 
                       document.querySelector(`[data-cart-item-key*="${itemKey}"]`);
          }
        }
      }
    }
    
    // If we can't find the specific item, show error at the top
    if (!targetItem) {
      targetItem = document.querySelector('.t4s-cartPage__items') || 
                  document.querySelector('[data-cart-items]') ||
                  document.body;
    }
    
    // Create error message
    const errorDiv = document.createElement('div');
    errorDiv.className = 'product-error-message';
    errorDiv.textContent = errorData.message || 'Not enough items available. Please reduce quantity.';
    
    // Insert error message near the product
    if (targetItem.classList.contains('t4s-page_cart__item')) {
      // Insert after the cart item
      targetItem.insertAdjacentElement('afterend', errorDiv);
    } else {
      // Insert at the beginning of the container
      targetItem.insertBefore(errorDiv, targetItem.firstChild);
    }
    
    // Auto-remove after 5 seconds
    setTimeout(() => {
      if (errorDiv.parentNode) {
        errorDiv.remove();
      }
    }, 5000);
  }

  async handleCartUpdate() {
    // Prevent infinite loops and rate limiting without dropping a real cart
    // mutation that lands during the cooldown window.
    const now = Date.now();
    if (this.isProcessing || (now - this.lastProcessTime) < this.processCooldown) {
      if (this.pendingCartUpdateTimeoutId) clearTimeout(this.pendingCartUpdateTimeoutId);
      const remainingCooldown = Math.max(250, this.processCooldown - (now - this.lastProcessTime) + 25);
      this.pendingCartUpdateTimeoutId = setTimeout(() => {
        this.pendingCartUpdateTimeoutId = null;
        this.handleCartUpdate();
      }, remainingCooldown);
      return;
    }
    
    this.isProcessing = true;
    this.lastProcessTime = now;
    
    try {
      // Get current cart
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();
      
      if (cart.items.length === 0) {
        document.querySelectorAll('.mini-cart-tier-progress').forEach((element) => element.remove());
        this.isProcessing = false;
        return;
      }
      
      const eligibleItems = [];
      for (const item of cart.items) {
        if (await this.isBulkEligible(item)) eligibleItems.push(item);
      }

      // Per-line progress belongs only on the cart page. The drawer uses its
      // dedicated aggregate progress component below.
      if (this.isCartPage()) this.scheduleTierProgressRender(eligibleItems);
      else setTimeout(() => this.updateMiniCartTierProgress(), 300);
      
      // Apply proper bulk pricing discounts
      this.applyBulkPricingDiscounts();
      
      // Safety re-render after DOM settles (still debounced)
      if (this.isCartPage()) {
        setTimeout(() => this.scheduleTierProgressRender(eligibleItems), 400);
      }
      
    } catch (error) {
      console.error('Error processing tier progress:', error);
    } finally {
      this.isProcessing = false;
    }
  }

  observeCartDomChanges() {
    // 2026-07-23 (P0 fix): DISABLED. This used to watch the cart-items
    // container for childList/subtree mutations and, on ANY mutation, fetch
    // /cart.js then render per-line tier progress. But showTierProgress()
    // appends its progress element INTO that same observed container, so the
    // observer re-fired on its own DOM write → fetch → render → fire … an
    // infinite loop that hammered /cart.js (~1 req/sec) and made quantity
    // edits impossible (the cart re-rendered continuously). The observer was
    // redundant anyway: real cart mutations already reach handleCartUpdate()
    // via the cart:updated/cart:refresh/cart:update-display listeners and the
    // fetch-wrapper success path — both cooldown-guarded. Kept as a safe
    // no-op so existing call sites don't break.
    try {
      if (this.cartDomObserver && typeof this.cartDomObserver.disconnect === 'function') {
        this.cartDomObserver.disconnect();
      }
    } catch (_) {}
    this.cartDomObserver = null;
  }

  scheduleTierProgressRender(cartItems) {
    try {
      if (this.tierRenderTimeoutId) {
        clearTimeout(this.tierRenderTimeoutId);
        this.tierRenderTimeoutId = null;
      }
      // Debounce to prevent flicker and duplicate renders
      this.tierRenderTimeoutId = setTimeout(() => {
        this.showTierProgress(cartItems);
        this.tierRenderTimeoutId = null;
      }, 180);
    } catch (_) {}
  }

  async processItemBulkPricing(item) {
    const quantity = item.quantity;
    let discountPercent = 0;
    
    // Determine discount based on individual product quantity
    if (quantity >= 20) {
      discountPercent = 7;
    } else if (quantity >= 10) {
      discountPercent = 5;
    } else if (quantity >= 5) {
      discountPercent = 3;
    } else if (quantity >= 2) {
      discountPercent = 2;
    }
    
    
    // Note: Bulk pricing is now handled by Liquid templates in cart-item.liquid
    // The JavaScript manager now only handles tier progress display
    this.showTierProgress([item]);
  }

  // KT-225: `applyDiscountCode()` and `refreshCart()` were DELETED here. Do not restore
  // them, and do not add a caller for anything like them.
  //
  // Both were already unreachable — nothing called applyDiscountCode, and refreshCart was
  // reachable only from it — so this removes no behaviour. They are gone because they were
  // a re-armed loop waiting for one call site:
  //
  //   refreshCart() dispatched `cart:update-display`
  //   ...and this class LISTENS to `cart:update-display` -> handleCartUpdate (see the
  //      constructor), which is what used to call back down into the discount path.
  //
  // That is the same cycle the 2026-07-23 P0 fixed, with window.location.reload() swapped
  // for a self-dispatched event: handleCartUpdate -> applyBulkPricingDiscounts -> (code
  // never applied) -> refreshCart -> re-enter handleCartUpdate, every 1-2 seconds. The
  // reload was removed; the cycle was left one function call from returning.
  //
  // Their presence had a second cost. Three separate reviews have pointed at this file as
  // the cause of a "cart keeps refreshing" report, because it contains discount code that
  // mentions refreshing. Each time the answer was that the code is unreachable. Deleting it
  // ends that: multi-buy is owned by the kt-quantity-breaks Shopify Function and this file
  // is DISPLAY-ONLY, with no cart writes at all (#370-#373).

  showTierProgress(cartItems) {
    // Define bulk pricing tiers
    const tiers = [
      { min: 2, discount: 2, name: '2% Discount' },
      { min: 5, discount: 3, name: '3% Discount' },
      { min: 10, discount: 5, name: '5% Discount' },
      { min: 20, discount: 7, name: '7% Discount' }
    ];

    cartItems.forEach(item => {
      const currentQuantity = item.quantity;
      const currentTier = this.getCurrentTier(currentQuantity, tiers);
      const nextTier = this.getNextTier(currentQuantity, tiers);
      
      if (nextTier) {
        const itemsNeeded = nextTier.min - currentQuantity;
        this.displayTierProgress(item, currentTier, nextTier, itemsNeeded);
      }
    });
  }

  getCurrentTier(quantity, tiers) {
    for (let i = tiers.length - 1; i >= 0; i--) {
      if (quantity >= tiers[i].min) {
        return tiers[i];
      }
    }
    return null;
  }

  getNextTier(quantity, tiers) {
    for (const tier of tiers) {
      if (quantity < tier.min) {
        return tier;
      }
    }
    return null; // Already at highest tier
  }

  displayTierProgress(item, currentTier, nextTier, itemsNeeded) {
    // Find the cart item element
    const cartItemElement = this.findCartItemElement(item);
    if (!cartItemElement) return;

    // Determine progress state
    const progressPercentage = nextTier ? Math.min((item.quantity / nextTier.min) * 100, 100) : 100;
    const isCloseToTier = nextTier ? (itemsNeeded <= 2) : false;
    const isAtTier = nextTier ? (itemsNeeded === 0) : true;

    // Update in place if exists to avoid flicker
    let progressMessage = cartItemElement.querySelector('.tier-progress-message');
    if (progressMessage) {
      progressMessage.className = `tier-progress-message ${isCloseToTier ? 'tier-close' : ''} ${isAtTier ? 'tier-reached' : ''}`;
      progressMessage.setAttribute('data-tier', nextTier ? nextTier.discount : '');
      progressMessage.setAttribute('data-item-key', item.key);

      const currentTierSpan = progressMessage.querySelector('.current-tier');
      const nextTierSpan = progressMessage.querySelector('.next-tier');
      const progressFill = progressMessage.querySelector('.tier-progress-fill');
      if (currentTierSpan) currentTierSpan.textContent = currentTier ? `Current: ${currentTier.name}` : 'No discount yet';
      if (nextTierSpan) {
        nextTierSpan.textContent = nextTier ? (isAtTier ? '🎉 Tier reached!' : `Add ${itemsNeeded} more for ${nextTier.name}`) : '';
        if (nextTier && !isAtTier) {
          nextTierSpan.classList.add('disabled');
          nextTierSpan.setAttribute('aria-disabled', 'true');
        } else {
          nextTierSpan.classList.remove('disabled');
          nextTierSpan.removeAttribute('aria-disabled');
        }
      }
      if (progressFill) progressFill.style.width = `${progressPercentage}%`;
      return;
    }

    // Create if not exists
    progressMessage = document.createElement('div');
    progressMessage.className = `tier-progress-message ${isCloseToTier ? 'tier-close' : ''} ${isAtTier ? 'tier-reached' : ''}`;
    progressMessage.setAttribute('data-tier', nextTier ? nextTier.discount : '');
    progressMessage.setAttribute('data-item-key', item.key);
    
    progressMessage.innerHTML = `
      <div class="tier-progress-container">
        <div class="tier-progress-info">
          <span class="current-tier">${currentTier ? `Current: ${currentTier.name}` : 'No discount yet'}</span>
          <span class="next-tier ${nextTier && !isAtTier ? 'disabled' : ''}" ${nextTier && !isAtTier ? 'aria-disabled="true"' : ''}>${nextTier ? (isAtTier ? '🎉 Tier reached!' : `Add ${itemsNeeded} more for ${nextTier.name}`) : ''}</span>
        </div>
        <div class="tier-progress-bar">
          <div class="tier-progress-fill" style="width: ${progressPercentage}%"></div>
        </div>
      </div>
    `;

    // Insert after the item info
    const itemInfo = cartItemElement.querySelector('.t4s-page_cart__info') || 
                     cartItemElement.querySelector('.t4s-cart-item-info') ||
                     cartItemElement.querySelector('[class*="cart-item"]');
    
    if (itemInfo) {
      itemInfo.appendChild(progressMessage);
    } else {
      // Fallback: append to the cart item itself
      cartItemElement.appendChild(progressMessage);
    }

  }

  updateProgressBar(itemKey, newQuantity) {
    // Find the progress bar for this item
    const progressMessage = document.querySelector(`[data-item-key="${itemKey}"]`);
    if (!progressMessage) return;

    // Calculate new tier information
    const tiers = [
      { min: 2, discount: 2, name: '2% Discount' },
      { min: 5, discount: 3, name: '3% Discount' },
      { min: 10, discount: 5, name: '5% Discount' },
      { min: 20, discount: 7, name: '7% Discount' }
    ];

    const currentTier = this.getCurrentTier(newQuantity, tiers);
    const nextTier = this.getNextTier(newQuantity, tiers);
    
    if (nextTier) {
      const itemsNeeded = nextTier.min - newQuantity;
      const progressPercentage = Math.min((newQuantity / nextTier.min) * 100, 100);
      const isCloseToTier = itemsNeeded <= 2;
      const isAtTier = itemsNeeded === 0;

      // Update the progress bar
      const progressFill = progressMessage.querySelector('.tier-progress-fill');
      const currentTierSpan = progressMessage.querySelector('.current-tier');
      const nextTierSpan = progressMessage.querySelector('.next-tier');

      if (progressFill) {
        progressFill.style.width = `${progressPercentage}%`;
      }

      if (currentTierSpan) {
        currentTierSpan.textContent = currentTier ? `Current: ${currentTier.name}` : 'No discount yet';
      }

      if (nextTierSpan) {
        nextTierSpan.textContent = isAtTier ? '🎉 Tier reached!' : `Add ${itemsNeeded} more for ${nextTier.name}`;
      }

      // Update classes
      progressMessage.className = `tier-progress-message ${isCloseToTier ? 'tier-close' : ''} ${isAtTier ? 'tier-reached' : ''}`;
      progressMessage.setAttribute('data-tier', nextTier.discount);

    }

    // Also update the bulk pricing display for this item
    this.updateBulkPricingForQuantity(itemKey, newQuantity);
  }

  findCartItemElement(item) {
    // Try different selectors to find the cart item element
    const selectors = [
      `[data-cart-item-key="${item.key}"]`,
      `[data-variant-id="${item.variant_id}"]`,
      `.t4s-page_cart__item:has([href*="${item.handle}"])`,
      `.cart-item:has([href*="${item.handle}"])`
    ];

    for (const selector of selectors) {
      const element = document.querySelector(selector);
      if (element) return element;
    }

    // Fallback: find by product title
    const allCartItems = document.querySelectorAll('.t4s-page_cart__item, .cart-item, [class*="cart-item"]');
    for (const cartItem of allCartItems) {
      if (cartItem.textContent.includes(item.title)) {
        return cartItem;
      }
    }

    return null;
  }

  initMiniCartTierProgress() {
    // Listen for mini cart updates
    document.addEventListener('cart:updated', () => {
      setTimeout(() => this.updateMiniCartTierProgress(), 500);
    });
    
    document.addEventListener('cart:refresh', () => {
      setTimeout(() => this.updateMiniCartTierProgress(), 500);
    });
    
    // Listen for mini cart opening to refresh tier progress
    const miniCartObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes') {
          const miniCart = mutation.target;
          if (miniCart.classList.contains('is-active') || miniCart.getAttribute('aria-hidden') === 'false') {
            // Mini cart just opened, update tier progress
            setTimeout(() => this.updateMiniCartTierProgress(), 300);
          }
        }
      });
    });
    
    // Observe mini cart element for class changes
    const miniCart = document.querySelector('#t4s-mini_cart, [data-mini-cart]');
    if (miniCart) {
      miniCartObserver.observe(miniCart, {
        attributes: true,
        attributeFilter: ['class', 'aria-hidden']
      });
    }
    
    // Initial mini cart tier progress
    setTimeout(() => this.updateMiniCartTierProgress(), 1500);
  }

  async updateMiniCartTierProgress() {
    try {
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();
      
      if (cart.items.length === 0) {
        // Remove tier progress if cart is empty
        const miniCart = document.querySelector('#t4s-mini_cart, .mini-cart, [class*="mini-cart"], [data-mini-cart]');
        if (miniCart) {
          const existingProgress = miniCart.querySelector('.mini-cart-tier-progress');
          if (existingProgress) {
            existingProgress.remove();
          }
        }
        return;
      }
      
      // Find mini cart element
      const miniCart = document.querySelector('#t4s-mini_cart, .mini-cart, [class*="mini-cart"], [data-mini-cart]');
      if (!miniCart) return;
      
      const eligibleItems = [];
      for (const item of cart.items) {
        if (await this.isBulkEligible(item)) eligibleItems.push(item);
      }

      // Calculate overall tier progress for products that actually participate.
      const tierProgress = this.calculateOverallTierProgress(eligibleItems);
      
      // Check if tier progress element already exists
      const existingProgress = miniCart.querySelector('.mini-cart-tier-progress');
      
      if (tierProgress) {
        if (existingProgress) {
          // Update existing element in place
          this.updateMiniCartTierProgressElement(existingProgress, tierProgress);
        } else {
          // Create new element
          this.displayMiniCartTierProgress(miniCart, tierProgress);
        }
      } else {
        // No tier progress needed, remove if exists
        if (existingProgress) {
          existingProgress.remove();
        }
      }
      
    } catch (error) {
      console.error('Error updating mini cart tier progress:', error);
    }
  }

  calculateOverallTierProgress(cartItems) {
    const tiers = [
      { min: 2, discount: 2, name: '2% Discount' },
      { min: 5, discount: 3, name: '3% Discount' },
      { min: 10, discount: 5, name: '5% Discount' },
      { min: 20, discount: 7, name: '7% Discount' }
    ];

    // Find the item closest to the next tier
    let closestItem = null;
    let closestNextTier = null;
    let minItemsNeeded = Infinity;

    cartItems.forEach(item => {
      const currentTier = this.getCurrentTier(item.quantity, tiers);
      const nextTier = this.getNextTier(item.quantity, tiers);
      
      if (nextTier) {
        const itemsNeeded = nextTier.min - item.quantity;
        if (itemsNeeded < minItemsNeeded) {
          minItemsNeeded = itemsNeeded;
          closestItem = item;
          closestNextTier = nextTier;
        }
      }
    });

    if (closestItem && closestNextTier) {
      return {
        item: closestItem,
        nextTier: closestNextTier,
        itemsNeeded: minItemsNeeded,
        progressPercentage: Math.min((closestItem.quantity / closestNextTier.min) * 100, 100)
      };
    }

    return null;
  }

  updateMiniCartTierProgressElement(existingElement, tierProgress) {
    const { item, nextTier, itemsNeeded, progressPercentage } = tierProgress;
    const isCloseToTier = itemsNeeded <= 2;
    const isAtTier = itemsNeeded === 0;

    // Update classes
    existingElement.className = `mini-cart-tier-progress ${isCloseToTier ? 'tier-close' : ''} ${isAtTier ? 'tier-reached' : ''}`;
    existingElement.setAttribute('data-tier', nextTier.discount);
    
    // Update message
    const messageElement = existingElement.querySelector('.mini-cart-tier-message');
    if (messageElement) {
      messageElement.textContent = isAtTier ? '🎉 Multi-buy tier reached!' : `Add ${itemsNeeded} more for ${nextTier.discount}% off this item`;
    }
    
    // Update progress bar
    const progressFill = existingElement.querySelector('.mini-cart-tier-fill');
    if (progressFill) {
      progressFill.style.width = `${progressPercentage}%`;
    }
  }

  displayMiniCartTierProgress(miniCart, tierProgress) {
    const { item, nextTier, itemsNeeded, progressPercentage } = tierProgress;
    const isCloseToTier = itemsNeeded <= 2;
    const isAtTier = itemsNeeded === 0;

    const progressElement = document.createElement('div');
    progressElement.className = `mini-cart-tier-progress ${isCloseToTier ? 'tier-close' : ''} ${isAtTier ? 'tier-reached' : ''}`;
    progressElement.setAttribute('data-tier', nextTier.discount);
    
    progressElement.innerHTML = `
      <div class="mini-cart-tier-container">
        <div class="mini-cart-tier-info">
          <span class="mini-cart-tier-title">Multi-buy saving</span>
          <span class="mini-cart-tier-message">${isAtTier ? '🎉 Multi-buy tier reached!' : `Add ${itemsNeeded} more for ${nextTier.discount}% off this item`}</span>
        </div>
        <div class="mini-cart-tier-bar">
          <div class="mini-cart-tier-fill" style="width: ${progressPercentage}%"></div>
        </div>
      </div>
    `;

    // Add mini cart specific styles
    progressElement.style.cssText = `
      margin: 10px 0;
      padding: 8px 12px;
      background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
      border: 1px solid #dee2e6;
      border-radius: 6px;
      font-size: 11px;
      color: #495057;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    `;

    // Insert into mini cart - try multiple selectors to find the right location
    const cartItems = miniCart.querySelector('.t4s-mini_cart__items, .mini-cart-items, .kt-mini-cart__items, [class*="cart-items"]');
    const cartFooter = miniCart.querySelector('.t4s-drawer__bottom, .mini-cart-footer, .kt-mini-cart__footer, [class*="cart-footer"], [class*="mini-cart__footer"]');
    
    if (cartFooter) {
      cartFooter.insertBefore(progressElement, cartFooter.firstChild);
    } else if (cartItems) {
      cartItems.appendChild(progressElement);
    } else {
      // Fallback: try to find content area
      const contentArea = miniCart.querySelector('.kt-mini-cart__content, [class*="content"]');
      if (contentArea) {
        contentArea.appendChild(progressElement);
      } else {
        miniCart.appendChild(progressElement);
      }
    }

  }

  updateCartItemDisplay(item, discountPercent) {
    // Find the cart item element
    const cartItemElement = this.findCartItemElement(item);
    if (!cartItemElement) return;

    // Note: Bulk pricing display is now handled by Liquid templates
    // This method is kept for compatibility but no longer updates pricing
  }

  updateBulkPricingDisplay(cartItemElement, item, discountPercent) {
    // Note: Bulk pricing display is now handled entirely by Liquid templates
    // This method is kept for compatibility but no longer creates display elements
  }

  updateBulkPricingForQuantity(itemKey, newQuantity) {
    // Note: Bulk pricing is now handled entirely by Liquid templates
    // This method is kept for compatibility but no longer updates display
  }

  removeOldBulkPricing(cartItemElement) {
    // Remove only cart-related bulk pricing elements from this specific cart item
    const selectors = [
      '.bulk-pricing-display',
      '.t4s-bulk-discount-badge',
      '[class*="discount-badge"]',
      '.t4s-cart_discount_price',
      '.t4s-order-discount__item'
    ];

    selectors.forEach(selector => {
      const elements = cartItemElement.querySelectorAll(selector);
      elements.forEach(element => {
        // Only remove if it's a cart-related bulk pricing element
        // Don't remove product page bulk pricing containers
        if ((element.textContent.includes('Bulk Pricing') || 
            element.textContent.includes('Bulk Discount') ||
            element.classList.contains('bulk-pricing-display')) &&
            !element.closest('.t4s-product__bulk-pricing') &&
            !element.closest('.bulk-pricing-container')) {
          element.remove();
        }
      });
    });
  }

  findCartItemElementByKey(itemKey) {
    // Try different selectors to find the cart item element by key
    const selectors = [
      `[data-cart-item-key="${itemKey}"]`,
      `[data-id="${itemKey}"]`,
      `[id*="${itemKey}"]`
    ];

    for (const selector of selectors) {
      const element = document.querySelector(selector);
      if (element) return element;
    }

    // Fallback: find by looking for the item key in the element
    const allCartItems = document.querySelectorAll('.t4s-page_cart__item, .cart-item, [class*="cart-item"]');
    for (const cartItem of allCartItems) {
      if (cartItem.getAttribute('data-cart-item-key') === itemKey ||
          cartItem.getAttribute('data-id') === itemKey) {
        return cartItem;
      }
    }

    return null;
  }

  formatMoney(amount) {
    // Format money amount - amount is already in pounds, not pence
    return `£${amount.toFixed(2)}`;
  }

  async applyBulkPricingDiscounts() {
    // 2026-07 (P0 root-cause fix): RETIRED. Multi-buy pricing is now owned by
    // the kt-quantity-breaks Shopify Function (extensions/kt-quantity-breaks),
    // which discounts each line server-side (2+/5+/10+/20+ -> 2/3/5/7%),
    // handles mixed-tier carts, and is rendered by main-cart.liquid via
    // line_level_discount_allocations.
    //
    // This legacy path applied cart-level BULK*PERCENT codes by POSTing
    // /cart/update.js and then re-dispatching `cart:refresh` — an event this
    // same manager listens to (handleCartUpdate). Because a cart-level code
    // cannot price a mixed/product-scoped cart, `appliedBulk` never matched the
    // desired code, so it re-applied forever: the continuous cart refresh and
    // the "can't change quantity" lock reported across #370-#373. (Its
    // Function stand-down check was also dead: it looked for an allocation
    // titled 'Multi-buy savings', but the Function emits 'Multi-buy N% off'.)
    //
    // The manager is now DISPLAY-ONLY (tier-progress hints). It performs no
    // cart mutations. Do not re-enable without deleting the cart-write +
    // self-dispatch re-entry entirely.
    return;
  }

  getTierCode(quantity) {
    if (quantity >= 20) return 'BULK7PERCENT';
    if (quantity >= 10) return 'BULK5PERCENT';
    if (quantity >= 5) return 'BULK3PERCENT';
    if (quantity >= 2) return 'BULK2PERCENT';
    return null;
  }

  async isBulkEligible(item) {
    const knownBrands = ['British General', 'Masterplug', 'Luceco'];
    if (knownBrands.includes(item.vendor)) return true;

    const handle = item.handle;
    if (!handle) return false;
    if (this.productEligibilityCache.has(handle)) {
      return this.productEligibilityCache.get(handle);
    }

    try {
      const response = await fetch(`/products/${encodeURIComponent(handle)}.js`);
      if (!response.ok) return false;
      const product = await response.json();
      const eligible = knownBrands.includes(product.vendor) ||
        (product.tags || []).some(tag => knownBrands.includes(tag));
      this.productEligibilityCache.set(handle, eligible);
      return eligible;
    } catch (_) {
      return false;
    }
  }

  async getDesiredBulkCode(cartItems) {
    const eligibleLines = [];
    for (const item of cartItems) {
      if (await this.isBulkEligible(item)) eligibleLines.push(item);
    }

    if (!eligibleLines.length) return null;
    const codes = eligibleLines.map(item => this.getTierCode(item.quantity || 0));
    if (codes.some(code => !code)) return null;
    const uniqueCodes = Array.from(new Set(codes));
    return uniqueCodes.length === 1 ? uniqueCodes[0] : null;
  }

  async updateCartDiscounts(_codes) {
    // 2026-07 (P0 root-cause fix): RETIRED with applyBulkPricingDiscounts.
    // This POSTed /cart/update.js (mutating the cart) and re-dispatched
    // `cart:refresh` into this manager's own listener — the re-entry that
    // caused the continuous cart refresh + quantity lock. Multi-buy pricing is
    // handled by the kt-quantity-breaks Shopify Function. No-op by design.
    return null;
  }

  cleanupOldBulkPricing() {
    // Remove only cart-related bulk pricing elements, not product page bulk pricing
    const selectors = [
      '.bulk-pricing-display',
      '.t4s-bulk-discount-badge',
      '[class*="discount-badge"]'
    ];

    selectors.forEach(selector => {
      const elements = document.querySelectorAll(selector);
      elements.forEach(element => {
        // Only remove if it's a cart-related bulk pricing element
        // Don't remove product page bulk pricing containers
        if ((element.textContent.includes('Bulk Pricing') || 
            element.textContent.includes('Bulk Discount') ||
            element.classList.contains('bulk-pricing-display')) &&
            !element.closest('.t4s-product__bulk-pricing') &&
            !element.closest('.bulk-pricing-container')) {
          element.remove();
        }
      });
    });

  }

  initQuantityChangeListeners() {
    // Listen for quantity input changes
    document.addEventListener('change', (e) => {
      if (e.target.matches('input[data-quantity-value], input[name="updates[]"], input[type="number"][min]')) {
        // Mark this input as recently updated
        e.target.dataset.lastUpdated = Date.now();
        
        const newQuantity = parseInt(e.target.value) || 1;
        const itemKey = e.target.getAttribute('data-id') || e.target.getAttribute('id');
        
        if (itemKey) {
          this.updateProgressBar(itemKey, newQuantity);
        }
        
        // Also trigger full cart update for comprehensive updates
        setTimeout(() => this.handleCartUpdate(), 100);
      }
    });

    // Listen for quantity button clicks
    document.addEventListener('click', (e) => {
      const quantityButton = e.target.closest('[data-quantity-selector], [data-increase-qty], [data-decrease-qty]');
      if (quantityButton) {
        // Mark the associated input as recently updated
        const quantityInput = e.target.closest('[data-cart-item]')?.querySelector('input[type="number"]');
        if (quantityInput) {
          quantityInput.dataset.lastUpdated = Date.now();
        }
        
        // Get the associated input field
        const button = quantityButton;
        const input = button.closest('.t4s-quantity-wrapper, .quantity-wrapper')?.querySelector('input[type="number"]');
        
        if (input) {
          const itemKey = input.getAttribute('data-id') || input.getAttribute('id');
          const currentQuantity = parseInt(input.value) || 1;
          
          // Predict the new quantity based on button type
          let newQuantity = currentQuantity;
          if (button.matches('[data-increase-qty], .is--plus')) {
            newQuantity = currentQuantity + 1;
          } else if (button.matches('[data-decrease-qty], .is--minus')) {
            newQuantity = Math.max(1, currentQuantity - 1);
          }
          
          if (itemKey && newQuantity !== currentQuantity) {
            // Update immediately for better UX
            this.updateProgressBar(itemKey, newQuantity);
          }
        }
        
        // Also trigger full cart update
        setTimeout(() => this.handleCartUpdate(), 200);
      }
    });

    // Listen for cart form submissions
    document.addEventListener('submit', (e) => {
      if (e.target.matches('form[action*="/cart"], form[data-cart-form]')) {
        setTimeout(() => this.handleCartUpdate(), 500);
      }
    });

    // Listen for input events (real-time updates)
    document.addEventListener('input', (e) => {
      if (e.target.matches('input[data-quantity-value], input[name="updates[]"], input[type="number"][min]')) {
        const newQuantity = parseInt(e.target.value) || 1;
        const itemKey = e.target.getAttribute('data-id') || e.target.getAttribute('id');
        
        if (itemKey && newQuantity > 0) {
          // Update progress bar in real-time as user types
          this.updateProgressBar(itemKey, newQuantity);
        }
      }
    });
  }
}

// Prevent multiple instances
if (!window.bulkPricingManager) {
// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
    if (!window.bulkPricingManager) {
      window.bulkPricingManager = new BulkPricingDiscountManager();
    }
});

// Also initialize if DOM is already loaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', function() {
      if (!window.bulkPricingManager) {
        window.bulkPricingManager = new BulkPricingDiscountManager();
      }
  });
} else {
    window.bulkPricingManager = new BulkPricingDiscountManager();
  }
}
